import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { firmAPI, userAPI } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const FirmMembers: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };
  const [members, setMembers] = useState<any[]>([]);
  const [firmName, setFirmName] = useState('');
  const [firmType, setFirmType] = useState<'client'|'vendor'|''>('');
  const [error, setError] = useState('');
  const [openAddExisting, setOpenAddExisting] = useState(false);
  const [existingUserId, setExistingUserId] = useState('');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userLoading, setUserLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    username: '',
    email: '',
    email_confirmation: '',
    job_title: '',
    contact_phone: '',
    welcome_message: '',
    // address fields for vendor
    country: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  const load = async () => {
    try {
      const res = await firmAPI.members(Number(firmId));
      setMembers(res.data.data || []);
      const firmRes = await firmAPI.getById(Number(firmId));
      setFirmName(firmRes.data.data.name);
      setFirmType(firmRes.data.data.type as 'client'|'vendor');
    } catch (e: any) {
      setError(e.message || 'Failed to load members');
    }
  };

  useEffect(() => { if (firmId) load(); }, [firmId]);

  const handleToggleFinance = async (userId: number) => {
    await firmAPI.toggleFinance(Number(firmId), userId);
    load();
  };
  const handleToggleActive = async (userId: number) => {
    await firmAPI.toggleActive(Number(firmId), userId);
    load();
  };
  const handleResetPassword = async (userId: number) => {
    try {
      const res = await firmAPI.resetPassword(Number(firmId), userId);
      setToastMsg(res.data?.message || 'Password reset and email sent');
      setToastOpen(true);
      load();
    } catch (e: any) {
      setToastMsg(e.response?.data?.message || 'Failed to reset password');
      setToastOpen(true);
    }
  };
  const handleRemoveMember = async (userId: number, userName: string) => {
    if (!window.confirm(`Remove ${userName} from this firm? They will lose access to this firm's cases and projects.`)) {
      return;
    }
    try {
      const res = await firmAPI.removeMember(Number(firmId), userId);
      setToastMsg(res.data?.message || 'Member removed from firm');
      setToastOpen(true);
      load();
    } catch (e: any) {
      setToastMsg(e.response?.data?.message || 'Failed to remove member');
      setToastOpen(true);
    }
  };
  const handleAddExisting = async () => {
    await firmAPI.addExistingMember(Number(firmId), Number(existingUserId));
    setOpenAddExisting(false);
    setExistingUserId('');
    load();
  };
  const handleCreate = async () => {
    await firmAPI.createMember(Number(firmId), createForm);
    setOpenCreate(false);
    setCreateForm({ name: '', username: '', email: '', email_confirmation: '', job_title: '', contact_phone: '', welcome_message: '', country: '', address_1: '', address_2: '', city: '', state: '', zip_code: '' });
    load();
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/firms'); }} sx={{ textDecoration: 'none' }}>Firms</Link>
        <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }} sx={{ textDecoration: 'none' }}>{firmName || `Firm #${firmId}`}</Link>
        <Typography color="text.primary">Members</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Firm Members</Typography>
        {isPmpAdminOrSiteAdmin() && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => setOpenAddExisting(true)}>Add Existing User</Button>
            <Button variant="contained" onClick={() => setOpenCreate(true)}>Add Firm User</Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Full Name</TableCell>
                 <TableCell>Status</TableCell>
                 <TableCell>Role</TableCell>
                <TableCell>Financial User</TableCell>
                {isPmpAdminOrSiteAdmin() && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>{m.id}</TableCell>
                  <TableCell>{m.username || '-'}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.status === 'active' ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>
                    {(() => {
                      const code = String(m.role || '').toLowerCase();
                        const map: Record<string,string> = {
                        pmp_admin: 'PMP Admin',
                        site_admin: 'Site Admin',
                        client_admin: 'Client Admin',
                        client_user: 'Client User',
                        client_manager: 'Client Manager',
                        client_supervisor: 'Client Supervisor',
                        vendor_admin: 'Vendor Admin',
                        vendor_user: 'Vendor Staff',
                        vendor_staff: 'Vendor Staff',
                        medical_user: 'Medical User',
                        internal_staff: 'Internal Staff',
                        internal_manager: 'Internal Manager',
                        legal_user: 'Legal User',
                        voice_user: 'Voice User',
                      };
                      return map[code] || (code ? code.replace('_',' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
                    })()}
                  </TableCell>
                  <TableCell>{m.is_finance_user ? 'Yes' : 'No'}</TableCell>
                  {isPmpAdminOrSiteAdmin() && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => handleToggleActive(m.id)}>{m.status === 'active' ? 'De-activate' : 'Activate'}</Button>
                        <Button size="small" onClick={() => handleToggleFinance(m.id)}>{m.is_finance_user ? 'Remove Finance' : 'Add as Financial User'}</Button>
                        <Button size="small" onClick={() => handleResetPassword(m.id)}>Reset Password</Button>
                        <Button size="small" color="error" onClick={() => handleRemoveMember(m.id, m.name)}>Remove from Firm</Button>
                        <Button size="small" onClick={() => navigate(`/users/${m.id}/profile`)}>Manage Profile</Button>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openAddExisting} onClose={() => setOpenAddExisting(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Existing User</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={userOptions}
            loading={userLoading}
            getOptionLabel={(opt) => `${opt.name} (${opt.email}${opt.username ? ` | ${opt.username}` : ''})`}
            onOpen={async () => {
              try {
                setUserLoading(true);
                const res = await userAPI.list({ status: 'active' });
                setUserOptions(res.data.data || []);
              } finally { setUserLoading(false); }
            }}
            onInputChange={async (_e, value) => {
              setUserSearch(value);
              try {
                setUserLoading(true);
                const res = await userAPI.list({ q: value });
                setUserOptions(res.data.data || []);
              } finally { setUserLoading(false); }
            }}
            onChange={(_e, value) => {
              setExistingUserId(value?.id ? String(value.id) : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                placeholder="Search by name, email, or username"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {userLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddExisting(false)}>Cancel</Button>
          <Button onClick={handleAddExisting} variant="contained" disabled={!existingUserId}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{firmType === 'vendor' ? 'Add Vendor Member' : 'Add Firm User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Full Name *" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} fullWidth />
            <TextField label="Username *" value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} fullWidth />
            <TextField label="Email *" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} fullWidth />
            <TextField label="Confirm Email *" value={createForm.email_confirmation} onChange={(e) => setCreateForm({ ...createForm, email_confirmation: e.target.value })} fullWidth />
            <TextField label="Job Title *" value={createForm.job_title} onChange={(e) => setCreateForm({ ...createForm, job_title: e.target.value })} fullWidth />
            <TextField label="Contact No *" value={createForm.contact_phone} onChange={(e) => setCreateForm({ ...createForm, contact_phone: e.target.value })} fullWidth />
            <TextField label="Welcome Message" value={createForm.welcome_message} onChange={(e) => setCreateForm({ ...createForm, welcome_message: e.target.value })} fullWidth multiline minRows={3} />
            {firmType === 'vendor' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Country *" value={createForm.country} onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })} fullWidth required />
                <TextField label="Zip Code *" value={createForm.zip_code} onChange={(e) => setCreateForm({ ...createForm, zip_code: e.target.value })} fullWidth required />
                <TextField label="Address 1 *" value={createForm.address_1} onChange={(e) => setCreateForm({ ...createForm, address_1: e.target.value })} fullWidth required />
                <TextField label="Address 2" value={createForm.address_2} onChange={(e) => setCreateForm({ ...createForm, address_2: e.target.value })} fullWidth />
                <TextField label="City *" value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} fullWidth required />
                <TextField label="State *" value={createForm.state} onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })} fullWidth required />
                <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>This member will be created as <strong>Vendor Staff</strong>.</Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        autoHideDuration={4000}
        message={toastMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default FirmMembers;


